<script setup>
import { computed, inject, ref, watch, onMounted, onUnmounted } from 'vue'
import { useStorage } from '@vueuse/core'
import { useAnomaliesStore } from '@/store/anomalies'
import SfdcImage from '@/assets/sfdc.png?w=42&h=42&format=webp&imagetools'
import HerokuImage from '@/assets/heroku.webp?w=42&h=42&format=webp&imagetools'
import LogoImage from '@/assets/logo.png?w=56&h=56&format=webp&imagetools'
import api from '@/lib/api'

const sidebarCollapsed = inject('sidebarCollapsed')
const version = import.meta.env.VITE_APP_VERSION
const anomaliesStore = useAnomaliesStore()
const currentEnvironment = useStorage('currentEnvironment', 'default')
const envOpts = ref([])
const currentEnvironmentLabel = computed(() => envOpts.value.find(x => x.value === currentEnvironment.value)?.label)

api.settings.getEnvironments().then((res) => {
  if (!res.find(y => y.value === currentEnvironment.value)) currentEnvironment.value = 'default'
  envOpts.value = res
})

anomaliesStore.fetchAnomalies()

const props = defineProps({
  embedded: {
    type: Boolean,
    default: false
  }
})

const items = ref([
  { icon: 'pi pi-chart-line', label: 'Dashboard', route: '/', colorClass: 'text-green-600' },
  { icon: 'pi pi-objects-column', label: 'All Mappings', route: '/objects', colorClass: 'text-sky-600' },
  { icon: 'pi pi-cloud-upload', label: 'Deploy', route: '/deploy', colorClass: 'text-blue-600' },
  { icon: 'pi pi-cog', label: 'Settings', route: '/settings', colorClass: 'text-yellow-600' },
])

const SECRET = 'turchettiparty'
const odataUnlocked = useStorage('odata-unlocked', false)
let typedBuffer = ''

const onKeydown = (e) => {
  // ignore modifier keys and non-character keys
  if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return
  typedBuffer += e.key.toLowerCase()
  if (typedBuffer.length > SECRET.length) {
    typedBuffer = typedBuffer.slice(-SECRET.length)
  }
  if (typedBuffer === SECRET) {
    odataUnlocked.value = true
    typedBuffer = ''
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

const allItems = computed(() => {
  if (!odataUnlocked.value) return items.value
  return [
    ...items.value,
    { icon: 'pi pi-database', label: 'OData', route: '/odata', colorClass: 'text-purple-600' },
  ]
})

const monitoring = ref([
  { icon: 'pi pi-exclamation-triangle', label: 'Anomalies', route: '/anomalies', colorClass: 'text-red-600', warning: false },
  { icon: 'pi pi-sync', label: 'Sync Logs', route: '/sync-logs', colorClass: 'text-fuchsia-600' },
  { icon: 'pi pi-info-circle', label: 'Operations Queue', route: '/operations-queue', colorClass: 'text-indigo-600' },
])

watch(anomaliesStore.status, (newValue) => { monitoring.value[0].warning = newValue.warning }, { immediate: true })

const externalLinks = [
  { icon: 'pi pi-file', label: 'API Docs', url: '/api-docs' },
  { iconType: 'img', icon: SfdcImage, alt: 'Sfdc Logo', label: 'Salesforce', url: 'https://lamborghini--uat.sandbox.lightning.force.com/one/one.app' },
  { iconType: 'img', icon: HerokuImage, alt: 'Heroku Logo', label: 'Heroku', url: 'https://dashboard.heroku.com/apps/syncfdy-uat/metrics' },
]

const windowTooSmall = inject('windowTooSmall')
</script>

<template>
  <nav
    :class="{
      hide: (sidebarCollapsed || windowTooSmall) && !props.embedded,
      sidebar: !embedded,
    }"
  >
    <div class="flex flex-col h-full">
      <div
        v-if="!embedded"
        class="flex items-center gap-2 px-6 shrink-0 border-b dark:border-b-gray-600 h-16"
      >
        <Avatar
          :image="LogoImage"
          role="img"
          aria-label="SyncFDY Logo"
        />
        <OverlayBadge
          v-if="currentEnvironment !== 'default' && currentEnvironmentLabel"
          size="small"
          class="fix-overlay-badge"
          :value="currentEnvironmentLabel"
        >
          <span class="font-semibold text-2xl text-primary">SyncFDY</span>
        </OverlayBadge>
        <span
          v-else
          class="font-semibold text-2xl text-primary"
        >SyncFDY</span>
      </div>
      <div class="overflow-y-auto">
        <ul class="list-none p-4 m-0">
          <li>
            <div class="p-3 flex items-center justify-between text-surface-600 dark:text-surface-400">
              <span class="font-medium">APPLICATION</span>
            </div>
            <ul class="list-none p-0 m-0 overflow-hidden">
              <li
                v-for="item of allItems"
                :key="item.label"
              >
                <router-link
                  v-slot="{ href, navigate, isActive }"
                  :to="item.route"
                  custom
                >
                  <a
                    v-ripple
                    :href
                    class="outline-none border-l-4 border-transparent flex items-center cursor-pointer p-3 rounded text-surface-700 hover:bg-surface-100 dark:text-surface-0 dark:hover:bg-surface-800 duration-150 transition-colors"
                    :class="{ 'border-l-violet-500': isActive, 'focus:border-violet-300': !isActive }"
                    @click="navigate"
                  >
                    <i
                      class="pi mr-2 text-green-600"
                      :class="[item.icon, item.colorClass]"
                    />
                    <span class="font-medium">{{ item.label }}</span>
                  </a>
                </router-link>
              </li>
            </ul>
          </li>
          <li>
            <div class="p-3 pt-8 flex items-center justify-between text-surface-600 dark:text-surface-400">
              <span class="font-medium">MONITORING</span>
            </div>
            <ul class="list-none p-0 m-0 overflow-hidden">
              <li
                v-for="item of monitoring"
                :key="item.label"
              >
                <router-link
                  v-slot="{ href, navigate, isActive }"
                  :to="item.route"
                  custom
                >
                  <a
                    v-ripple
                    :href
                    class="outline-none border-l-4 border-transparent flex items-center cursor-pointer p-3 rounded text-surface-700 hover:bg-surface-100 dark:text-surface-0 dark:hover:bg-surface-800 duration-150 transition-colors justify-between"
                    :class="{ 'border-l-violet-500': isActive, 'focus:border-violet-300': !isActive }"
                    @click="navigate"
                  >
                    <div>
                      <i
                        class="pi mr-2 text-green-600"
                        :class="[item.icon, item.colorClass]"
                      />
                      <span class="font-medium">{{ item.label }}</span>
                    </div>
                    <div
                      v-if="item.warning"
                      class="bg-red-500 flashit absolute"
                      style="border-radius: 50%; top: 14px; right: 14px; padding: 0.45rem"
                    />
                  </a>
                </router-link>
              </li>
            </ul>
          </li>
          <li>
            <div class="p-3 pt-8 flex items-center justify-between text-surface-600 dark:text-surface-400">
              <span class="font-medium">OTHER RESOURCES</span>
            </div>
          </li>
          <li
            v-for="item of externalLinks"
            :key="item.label"
          >
            <a
              v-ripple
              :href="item.url"
              target="_blank"
              class="outline-none focus:border-violet-300 border-l-4 border-transparent flex items-center justify-between cursor-pointer p-3 rounded text-surface-700 hover:bg-surface-100 dark:text-surface-0 dark:hover:bg-surface-800 duration-150 transition-colors p-ripple"
            >
              <span class="flex gap-2">
                <i
                  v-if="item.iconType !== 'img'"
                  class="pi mr-2 text-teal-600"
                  :class="item.icon"
                />
                <img
                  v-else
                  :alt="item.alt"
                  :src="item.icon"
                  class="w-6 h-6"
                >
                <span class="font-medium">{{ item.label }}</span>
              </span>
              <i class="pi pi-external-link" />
            </a>
          </li>
          <li><Divider /></li>
          <li class="px-4">
            <FloatLabel
              class="w-full"
              variant="on"
            >
              <Select
                v-model="currentEnvironment"
                label-id="environment"
                :options="envOpts"
                option-label="label"
                option-value="value"
                class="w-full"
              />
              <label for="environment">Environment</label>
            </FloatLabel>
          </li>
        </ul>
      </div>
      <div class="font-mono flex-grow flex items-end pl-8 pb-4">
        <span>v{{ version }}</span>
      </div>
    </div>
  </nav>
</template>
<style lang="postcss">
.sidebar {
  @apply w-72;
  @apply h-full;
  @apply min-h-52;
  @apply fixed;
  @apply top-[64x];
  @apply left-0;
  transition: left 0.2s;
  @apply bg-white;
  @apply dark:bg-[#1f1f1f]
}

.sidebar.hide {
  @apply -left-72;
}

.flashit{
  animation: flash linear 2s infinite;
}

.fix-overlay-badge.p-overlaybadge .p-badge {
  transform: translate(67%,-40%);
}
</style>
